import { Box, Button, Card, CardHeader, CircularProgress, Divider, Grid, IconButton, Popover, Stack, Tooltip, Typography } from "@mui/material";
import { CRUD_GET_LIST, ListContext, ListControllerProps, RecordContextProvider, ReduxState, crudGetOne, useGetMainList, useRecordContext, useSaveContext } from "ra-core";
import { FC, useMemo, useState, useContext } from "react";
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Helmet } from 'react-helmet-async'
import { useDispatch, useSelector } from "react-redux";
import { useHasPermit } from "~/authProvider/useHasPermit";
import { Content } from "~/components/content/Content";
import { useCollectionSelectionCard } from "~/components/obfuscation/CollectionSelectionCard";
import { EDIT_DATA_CLASSIFICATION_ENCRYPTION } from "~/constants";
import Create from "~/controller/Create";
import { useSimpleSaveAction } from "~/controller/SaveContextProvider";
import { RecPatch } from "~/dataProvider/tools";
import { FileColumn, FileEncryptionTemplateDto, FileTemplateKind, PrimaryClassification } from "~/gen/api/encrypt";
import { CollectionSchemaResponseDto, EditCollectionMappingInput, EncryptionMappingResponseDto, PropertyMappingResponseDto } from "~/gen/api/governance";
import { DatabaseType } from "~/gen/api/backup";
import { OdResourcePageProps } from "~/types/resource";
import { pages, sorts } from "~/utils/paramsWithGetMainList";
import { useMappingEditParams, useOptionsData } from "./FileTemplateDetail.hooks";
import JsonMappingViewer from "./FileTemplateDetail.JsonMappingViewer";
import CreateOrEdit from "~/controller/CreateOrEdit";
import { DropEvent, useDropzone } from "react-dropzone";
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers";
import { crudGetOneEx } from "~/actions/crudFetchEx";
import { useSnackbar } from "notistack";


const EncryptMappingRes = 'encryption/mappings'

const EditNoSqlEncryptionMappings: FC<OdResourcePageProps&RecPatch> = ({
  id,
  options,
  resource,
}) => {
  const databaseEm = useSchemaReadyDatabase(id);
  const [collection, setCollection] = useState(undefined as CollectionSchemaResponseDto|undefined)
  const { CollectionSelectionCard, refetch: collectionsRefetch } = useCollectionSelectionCard(databaseEm, resource === EncryptMappingRes ? "collections" : 'pending-collections')

  const { data, ids, refetch: propertiesRefetch } = useGetMainList<PropertyMappingResponseDto&RecPatch>(
    databaseEm && collection ? 'collection-properties' : 'noop', pages(), sorts(),
    { dbId: collection?.databaseId, tableId: collection?.collectionId }, { action: CRUD_GET_LIST }
  )
  const listCtx = useMemo(() => ({
    refetch: () => {
      collectionsRefetch()
      propertiesRefetch()
    }
  }) as any, [collectionsRefetch, propertiesRefetch])

  const jsonTmplObj = useMemo(() => {
    const x = {
      templateId: collection?.collectionId,
      name: collection?.name,
      kind: FileTemplateKind.JsonSample,
      encryptionKeyId: 1,
      columns: (ids ?? []).flatMap(id => {
        const gp = DB_GeneratedProps[databaseEm?.databaseType!]
        const p = data![id]
        if (!p || (gp && gp.has(p.name!))) { return [] }
        const y: FileColumn = {
          columnName: p.name,
          order: p.propertyId,
          primaryClassification: p.primaryClassification,
          secondaryClassificationId: p.secondaryClassificationId,
          dataType: p.type,
          precision: p.precision,
          scale: p.scale,
          encryptionRuleId: p.encryptionRuleId,
          encryptor: p.encryptor,
          obfuscationRuleId: p.obfuscationRuleId,
        }
        return [y]
      }),
      jsonSchema: collection?.jsonSchema,
    } as FileEncryptionTemplateDto
    return x
  }, [databaseEm, collection, data, ids])

  const handlers = useDefaultSavedHandlers("Edit Encryption Mappings")
  return (
    <Content 
      pageName="Edit Encryption Mappings" 
      resourceName="Edit Encryption Mappings" 
      breadcrumbs={[ ...(options?.breadcrumbs || []), { label: 'Encryption Mappings', path: `/${EncryptMappingRes}` } ]}
    >
      <Helmet>
        <title>Edit Encryption Mappings | OnData</title>
      </Helmet>

      <Grid container spacing={4}>
        <Grid item xs={12}>
          <RecordContextProvider value={databaseEm}>
            <CollectionSelectionCard {...{collection, setCollection}} />
          </RecordContextProvider>
        </Grid>
        <Grid item xs={12}>
          <ListContext.Provider value={listCtx}>
            <Create resource="encryption/mappings/nosql" basePath="" {...handlers}>
              <RecordContextProvider value={collection}>
                <NoSqlCollectionPropertyMappings jsonTmplObj={jsonTmplObj} />
              </RecordContextProvider>
            </Create>
          </ListContext.Provider>
        </Grid>
      </Grid>
    </Content>
  )
}

export default EditNoSqlEncryptionMappings;


const NoSqlCollectionPropertyMappings: FC<{jsonTmplObj: FileEncryptionTemplateDto}> = ({jsonTmplObj}) => {
  const hasWritePermit = useHasPermit([EDIT_DATA_CLASSIFICATION_ENCRYPTION])
  const save = useSimpleSaveAction()
  const { saving } = useSaveContext()
  const { refetch } = useContext<ListControllerProps>(ListContext)
  
  const optionsData = useOptionsData()
  const { errors, clearErrors, validateColumn } = useMappingValidation()
  const mappingEditParams = useMappingEditParams({record: jsonTmplObj, onValueEdit: validateColumn})
  const { gatherEdits, touched, discardAllEdits, version } = mappingEditParams
  const [outdateVersion, setOutdateVer] = useState(-1)
  
  const collection : CollectionSchemaResponseDto = useRecordContext()

  const saveMappings = () => {
    if (!touched() || saving) { return }
    const edits = gatherEdits()
    const payload : EditCollectionMappingInput = {
      databaseId: collection.databaseId,
      collectionId: collection.collectionId!,
      propertyMappings: (edits.columns ?? []).map(c => ({
        propertyId: c.order,
        isSkipped: Boolean(
          (c.secondaryClassificationId && !c.encryptionRuleId) || 
          c.secondaryClassificationId === undefined && c.encryptionRuleId === null
        ),
        primaryClassification: c.primaryClassification,
        secondaryClassificationId: c.secondaryClassificationId,
        encryptionRuleId: c.encryptionRuleId,
        obfuscationRuleId: c.obfuscationRuleId,
      }))
    }

    save(payload, {
      onSuccess: () => {
        refetch && refetch()
        setOutdateVer(version)
      }
    })
  }

  const handlers = useDefaultSavedHandlers("Upload sample data")
  return (
    <Card>
      <CardHeader 
        title={
          <Stack direction="row" >
            <span>Mappings</span>
            <Box sx={{ ml: 2 }}>
              <CreateOrEdit resource="collections" basePath="" record={collection} {...handlers}>
                <UploadCollectionSampleJson />
              </CreateOrEdit>
            </Box>
          </Stack>
        } 
        action={
          hasWritePermit && touched() &&
          <Stack direction="row" spacing={2}>
            <Button
              disabled={saving || outdateVersion === version}
              variant="outlined"
              onClick={() => { 
                discardAllEdits()
                clearErrors()
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={!!errors || saving || outdateVersion === version}
              variant="contained"
              onClick={() => saveMappings()}
            >
              Save
            </Button>
          </Stack> 
        }
      />
      <Divider />
      <JsonMappingViewer record={jsonTmplObj} optionsData={optionsData} mappingEditParams={mappingEditParams} errors={errors} />
    </Card>
  )
}

const DB_GeneratedProps : Record<DatabaseType, Set<string>|undefined> = {
  // [DatabaseType.CosmosDB]: new Set(["$._rid", "$._self", "$._etag", "$._attachments", "$._ts",]) // disable the check as of now, 2023-04-07
} as any

const UploadCollectionSampleJson : FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const save = useSimpleSaveAction()
  const { saving } = useSaveContext()
  const { refetch } = useContext<ListControllerProps>(ListContext)

  const collection : CollectionSchemaResponseDto = useRecordContext()
  const onDropAccepted = (files: File[], event: DropEvent) => {
    const file = files.pop()
    if (!file) { return }
    const payload = {
      databaseId: collection.databaseId,
      collectionId: collection.collectionId,
      sampleJson: file,
    }
    save(payload, { 
      onSuccess: () => { setAnchorEl(null); refetch()},
      onFailure: () => { setAnchorEl(null)},
    })
  }
  const { enqueueSnackbar } = useSnackbar()
  const { getRootProps, getInputProps } = useDropzone({
    multiple: false,
    maxFiles: 1,
    noDrag: true,
    accept: ['.json', '.txt'],
    maxSize: 2097152, // 2MB
    onDropAccepted,
    onDropRejected(fileRejections) {
      if (fileRejections.length > 0) {
        enqueueSnackbar('Can not upload an image larger than 2MB', { variant: 'error' })
      }
    },
  })

  return (
    <Box>
      <Tooltip title="Collection schema isn't quite right?">
        <IconButton size="small" onClick={handleClick} color="secondary">
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        PaperProps={{ sx: { p: 2 }}}
      >
        <Typography variant="subtitle2">Collection schema isn't quite right?</Typography>
        <Typography variant="caption" component="p" sx={{ mb: 2 }}>You can try upload a new sample json file with correct data structure.</Typography>
        
        <Button
          disabled={saving}
          variant="contained"
          size="small"
          color="primary"
          arial-label="upload values file"
          {...(getRootProps() as any)}
          startIcon={
            saving ? 
              <CircularProgress color="info" size="small" sx={{ width: 24 }} /> : 
              <FolderOpenIcon />
            }
        >
          <input {...getInputProps()} />
          Upload Sample...
        </Button>
      </Popover>
    </Box>
  )
}

interface MappingValidationProps { 
  errors: Record<string, object>|null; 
  clearErrors: ()=>void; 
  validateColumn: (name:string, column:FileColumn)=>void; 
}

const useMappingValidation = (): MappingValidationProps => {
  const [states, setStates] = useState({
    errors: null as Record<string, object>|null,
  })

  return ({
    errors: states.errors,
    ...useMemo(() => ({
      validateColumn: (name:string, column:FileColumn) => {
        const arr: any[] = []
        if (column.secondaryClassificationId) {
          if (column.primaryClassification === PrimaryClassification.Public) {
            if (column.encryptionRuleId) {
              arr.push({ encryptionRuleId: 'Should just "Skip"' })
            }
          }
        } else if (column.encryptionRuleId || column.obfuscationRuleId) {
          arr.push({ secondaryClassificationId: 'Classification is required' })
        }
        if (arr.length > 0) {
          setStates(v => ({
            errors: {...v.errors,
              [name]: arr.reduce((acc, x) => ({...acc, ...x}), {} as object)
            },
          }))
        } else {
          setStates(v => {
            if (v.errors) {
              delete v.errors[name]
              return Object.keys(v.errors).length === 0
                ? {errors:null} : {errors: {...v.errors}}
            } else {
              return v
            }
          })
        }
      },
      clearErrors: () => setStates({ errors: null })
    }), [setStates])
  })  
}

export const useSchemaReadyDatabase = (id: any) => {
  const dispatch = useDispatch();
  const [prev, setPrev] = useState(null as any)
  const databaseEm = useSelector<ReduxState, EncryptionMappingResponseDto | undefined>((store) => {
    const x = store.admin.resources[EncryptMappingRes].data[id] as any;
    if (!x) {
      setTimeout(() => setPrev((prev:any) => {
        if (prev !== id) {
          dispatch(crudGetOneEx(EncryptMappingRes, id, ''));
          return id
        } else {
          return prev
        }
      }))
    }
    return x;
  });
  return databaseEm;
}

